"use client";

import { useMutation } from "@tanstack/react-query";
import type {
  CampaignApplicationResponse,
  CampaignApplicationVO,
  CreateOrUpdateApplicationRequest,
} from "@trex-2b/api/src/trex-api/trex-api";
import { trexAuthProxy } from "@trex-2b/libs/config";
import {
  type ApplicationMutationBugsnagMeta,
  reportGrowthPortalError,
  resolveApplicationMutationFlow,
} from "@trex-2b/libs/utils/bugsnag-report";

/** create：完整 API envelope（调用处可用 `response.obj`）；update：解包后的申请单 VO */
export type ApplicationMutationResult =
  | {
      mode: "create";
      applicationId?: string;
      response: CampaignApplicationResponse | undefined;
    }
  | {
      mode: "update";
      applicationId?: string;
      response: CampaignApplicationVO | null | undefined;
    };

type ApplicationMutationVariables =
  | {
      kind: "create";
      data: CreateOrUpdateApplicationRequest;
      bugsnag?: ApplicationMutationBugsnagMeta;
    }
  | {
      kind: "update";
      applicationId: string;
      data: CreateOrUpdateApplicationRequest;
      bugsnag?: ApplicationMutationBugsnagMeta;
    };

function reportApplicationMutationError(
  error: unknown,
  vars: ApplicationMutationVariables,
): void {
  const flow = resolveApplicationMutationFlow(vars, vars.bugsnag?.flow);
  const baseContext =
    vars.kind === "update"
      ? {
          application_id: vars.applicationId,
          intent: vars.data.intent,
        }
      : { intent: vars.data.intent };

  reportGrowthPortalError(flow, error, {
    ...baseContext,
    ...vars.bugsnag?.context,
  });
}

/**
 * 投放申请写入：Start PRISM 用 create，后续步骤用 update。
 * 调用处请用 createApplication / updateApplication，避免靠「有没有 id」猜语义。
 */
export function useApplicationMutation() {
  const mutation = useMutation<
    ApplicationMutationResult,
    Error,
    ApplicationMutationVariables
  >({
    mutationFn: async (vars): Promise<ApplicationMutationResult> => {
      if (vars.kind === "update") {
        const response = await trexAuthProxy.v1.updateApplication(
          vars.applicationId,
          vars.data,
        );
        if (response.data.success === false) {
          throw new Error(response.data.message || "updateApplication failed");
        }
        return {
          mode: "update",
          applicationId: vars.applicationId,
          response: response.data.obj,
        };
      }

      const response = await trexAuthProxy.v1.createApplication(vars.data);
      if (response.data.success === false) {
        throw new Error(response.data.message || "createApplication failed");
      }
      return {
        mode: "create",
        applicationId: response.data.obj?.id,
        response: response.data,
      };
    },
    onError: (error, vars) => {
      reportApplicationMutationError(error, vars);
    },
  });

  return {
    applicationMutation: mutation,
    createApplication: (
      data: CreateOrUpdateApplicationRequest,
      bugsnag?: ApplicationMutationBugsnagMeta,
    ) =>
      mutation.mutateAsync({
        kind: "create",
        data,
        bugsnag,
      }) as Promise<Extract<ApplicationMutationResult, { mode: "create" }>>,
    updateApplication: (
      applicationId: string,
      data: CreateOrUpdateApplicationRequest,
      bugsnag?: ApplicationMutationBugsnagMeta,
    ) =>
      mutation.mutateAsync({
        kind: "update",
        applicationId,
        data,
        bugsnag,
      }) as Promise<Extract<ApplicationMutationResult, { mode: "update" }>>,
  };
}
